import { Trans, useLocale } from '~/common/context';
import { useFormContext } from 'react-hook-form';
import { isNil } from 'lodash-es';
import { DEFAULT_EURO_SIGN } from '@wyz/utils';
import { QuoteHelpers } from '~/purchase/helpers';
import { Flex, Icon } from '@wyz/ui';
import { NumberField } from '~/common/components';
import clsx from 'clsx';

type PriceInputProps = {
  baseName: string;
  item: {
    currency: string;
    quantity: number;
    unitPriceTaxIncl: number;
    discountBasePrice: number | null; // base price to compute discount
    marginBasePrice: number | undefined; // base price to compute margin
  };
  vatPercentage: number;
  adjustmentType: 'MARGIN' | 'DISCOUNT';
  itemType: 'TYRE' | 'SERVICE';
};

function PriceInput({
  baseName,
  item,
  vatPercentage,
  adjustmentType,
  itemType = 'TYRE',
}: PriceInputProps) {
  const methods = useFormContext();
  const quantityName = `${baseName}.quantity`;
  const discountedPriceName = `${baseName}.discountedPriceTaxIncl`;
  const discountName = `${baseName}.discount`;
  const marginName = `${baseName}.margin`;
  const locale = useLocale();
  const currencyCode = item.currency
    ? locale.formatters.currency(item.currency)
    : DEFAULT_EURO_SIGN;
  const onDiscountedPriceChange = (priceTaxIncl: number) => {
    methods.setValue(baseName, {
      ...QuoteHelpers.getQuotePriceFieldValues(
        priceTaxIncl / item.quantity,
        item.discountBasePrice,
        item.marginBasePrice,
        vatPercentage,
        item.quantity,
        adjustmentType,
      ),
      discountedPriceTaxIncl: priceTaxIncl,
    });
  };

  const onDiscountChange = (discount: number) => {
    if (discount === 0) {
      methods.setValue(baseName, {
        ...QuoteHelpers.getQuotePriceFieldValues(
          item.unitPriceTaxIncl,
          item.discountBasePrice,
          item.marginBasePrice,
          vatPercentage,
          item.quantity,
          adjustmentType,
        ),
        discount,
      });
    } else if (!isNil(item.discountBasePrice)) {
      const priceTaxExcl = item.discountBasePrice * ((100 - discount) / 100);
      methods.setValue(baseName, {
        ...QuoteHelpers.getQuotePriceFieldValues(
          QuoteHelpers.getPriceTaxIncl(priceTaxExcl, vatPercentage),
          item.discountBasePrice,
          item.marginBasePrice,
          vatPercentage,
          item.quantity,
          adjustmentType,
        ),
        discount,
      });
    }
  };

  const onMarginChange = (margin: number) => {
    if (isNil(item.marginBasePrice)) {
      return;
    }
    const marginOneItem =
      QuoteHelpers.getPriceTaxExcl(margin, vatPercentage) / item.quantity;
    let priceTaxExcl =
      adjustmentType === 'MARGIN'
        ? item.marginBasePrice + marginOneItem
        : item.marginBasePrice - marginOneItem;

    // Ensure price does not become negative
    priceTaxExcl = Math.max(0, priceTaxExcl);

    methods.setValue(baseName, {
      ...QuoteHelpers.getQuotePriceFieldValues(
        QuoteHelpers.getPriceTaxIncl(priceTaxExcl, vatPercentage),
        item.discountBasePrice,
        item.marginBasePrice,
        vatPercentage,
        item.quantity,
        adjustmentType,
      ),
      margin,
    });
  };

  return (
    <Flex className='gap-2' alignItems='end'>
      <input type='number' {...methods.register(quantityName)} hidden />
      <div>
        <NumberField
          sign={currencyCode}
          name={discountedPriceName}
          label={
            <Trans i18nKey='app.purchase.quote_cart_page.price_edit.discounted_price' />
          }
          withKnobs={false}
          min={0}
          onChangeValue={(value) => value && onDiscountedPriceChange(value)}
        />
      </div>
      <div className='mb-2'>
        <Icon icon='arrow-left-right-line' size='xs' />
      </div>
      <div>
        <NumberField
          sign='%'
          name={discountName}
          label={
            <Trans
              i18nKey={
                itemType === 'SERVICE'
                  ? 'app.purchase.quote_cart_page.price_edit.service_discount'
                  : 'app.purchase.quote_cart_page.price_edit.bf_discount'
              }
            />
          }
          withKnobs={false}
          min={0}
          max={100}
          onChangeValue={(value) =>
            !isNil(value) && onDiscountChange(Math.min(100, Math.max(0, value)))
          }
          disabled={!item.discountBasePrice}
        />
      </div>
      <div
        className={clsx('mb-2', {
          invisible: isNil(item.marginBasePrice),
        })}
      >
        <Icon icon='arrow-left-right-line' size='xs' />
      </div>
      <div
        className={clsx({
          invisible: isNil(item.marginBasePrice),
        })}
      >
        <NumberField
          sign={currencyCode}
          name={marginName}
          label={
            adjustmentType === 'MARGIN' ? (
              <Trans i18nKey='app.purchase.quote_cart_page.price_edit.margin' />
            ) : (
              <Trans
                i18nKey='app.purchase.quote_cart_page.price_edit.discount'
                values={{ currency: currencyCode }}
              />
            )
          }
          withKnobs={false}
          onChangeValue={(value) => value && onMarginChange(value)}
        />
      </div>
    </Flex>
  );
}

export default PriceInput;
